import { type Attachment } from '@clawlet/gateway-client';
import { KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AttachmentPreviewBar from './AttachmentPreviewBar';
import BuilderStatusBar from './BuilderStatusBar';
import ContextTokenWarningDialog from './ContextTokenWarningDialog';
import MarkdownRenderer from './MarkdownRenderer';
import SubagentBar from './SubagentBar';
import TaskStatusPanel from './TaskStatusPanel';
import ThinkingIndicator from './ThinkingIndicator';
import TodoTaskList from './TodoTaskList';
import ToolCallSection from './ToolCallSection';
import WelcomeScreen from './WelcomeScreen';
import { useFileAttachment } from '../hooks/useFileAttachment';
import { useIsMobile } from '../hooks/useIsMobile';
import { useVoice } from '../hooks/useVoice';
import { agentIdFromSessionKey, useAppStore, type TodoItem, type ToolCallEntry } from '../stores/appStore';
import { useSettingsStore } from '../stores/settingsStore';
import { toFriendlyErrorMessage } from '../utils/friendlyErrors';
import {
  CHAT_UPLOAD_ACCEPT,
  MAX_CHAT_ATTACHMENTS,
  appendUploadedAttachmentsToMessage,
  uploadPendingAttachments,
} from '../utils/chatUploads';
import { getSessionTokenCount, shouldShowContextTokenWarning } from '../utils/contextTokenWarning';
import { formatTimestamp, shouldShowTimestamp } from '../utils/timestampUtils';

interface ChatPanelProps {
  connected: boolean;
  sendMessage: (text: string, attachments?: Attachment[]) => Promise<void>;
  sendBtwMessage: (text: string) => Promise<{ sessionKey: string; label: string }>;
  abortChat: (sessionKey: string) => Promise<{ ok: boolean; status?: string }>;
}

const AUTO_SCROLL_THRESHOLD_PX = 100;
const EMPTY_WARNINGS: { text: string; code: string }[] = [];
const EMPTY_TODOS: TodoItem[] = [];

function isAgentToolCall(tc: ToolCallEntry): boolean {
  return tc.name === 'Agent' || tc.name === 'collab';
}

function isNearBottom(element: HTMLDivElement): boolean {
  return element.scrollHeight - (element.scrollTop + element.clientHeight) <= AUTO_SCROLL_THRESHOLD_PX;
}

function InjectedContextToggle({ content }: { content: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 transition-colors"
        style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--theme-muted-foreground)' }}
      >
        <span style={{ fontSize: '0.55rem' }}>{expanded ? '▼' : '▶'}</span>
        <span>// Injected Context</span>
      </button>
      {expanded && (
        <div
          className="mt-1.5 max-h-64 overflow-y-auto rounded-lg px-3 py-2 text-xs"
          style={{ border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(37, 42, 64, 0.5)', color: 'var(--theme-muted-foreground)' }}
        >
          <MarkdownRenderer content={content} />
        </div>
      )}
    </div>
  );
}

function CommittedAgentOutput({
  agentToolCalls,
  subagentToolCalls,
}: {
  agentToolCalls: ToolCallEntry[];
  subagentToolCalls: ToolCallEntry[];
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 transition-colors"
        style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--theme-muted-foreground)' }}
      >
        <span style={{ fontSize: '0.55rem' }}>{expanded ? '▼' : '▶'}</span>
        <span>// Agent Output</span>
      </button>
      {expanded && (
        <div className="mt-1.5 max-h-64 overflow-y-auto rounded-lg px-3 py-2" style={{ border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(37, 42, 64, 0.5)' }}>
          {agentToolCalls.length > 0 ? (
            <ToolCallSection entries={agentToolCalls} isCommitted title="// Agent" />
          ) : null}
          {subagentToolCalls.length > 0 ? (
            <ToolCallSection entries={subagentToolCalls} isCommitted title={`// Tool Calls (${subagentToolCalls.length})`} />
          ) : null}
        </div>
      )}
    </div>
  );
}

function AgentOutputBox({
  isRunning,
  resultText,
  subagentToolCalls,
}: {
  isRunning: boolean;
  resultText: string | null;
  subagentToolCalls: ToolCallEntry[];
}) {
  const [expanded, setExpanded] = useState(!false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll while streaming
  useEffect(() => {
    if (expanded && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [resultText, subagentToolCalls, expanded]);

  const activeToolCount = subagentToolCalls.filter((tc) => tc.status === 'running').length;

  // Collapsed toggle when done
  if (!isRunning && !expanded) {
    return (
      <div className="mt-1">
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex items-center gap-1.5 transition-colors"
          style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--theme-muted-foreground)' }}
        >
          <span style={{ fontSize: '0.55rem' }}>▶</span>
          <span>// Agent Output</span>
        </button>
      </div>
    );
  }

  return (
    <div
      className="max-w-full rounded-lg px-4 py-2 text-sm"
      style={{
        border: '1px solid rgba(255,255,255,0.06)',
        background: 'var(--theme-secondary)',
        borderLeft: '2px solid rgba(255, 123, 53, 0.3)',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 w-full"
      >
        <span
          className={isRunning ? 'thinking-pulse' : undefined}
          style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--theme-muted-foreground)' }}
        >
          // Agent Output
        </span>
        <span className="ml-auto" style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.55rem', color: 'var(--theme-muted-foreground)' }}>
          {expanded ? '▼' : '▶'}
        </span>
      </button>
      {expanded && (
        <div ref={contentRef} className="mt-2 max-h-64 overflow-y-auto">
          {resultText ? (
            <div
              className="thinking-content rounded-lg px-3 py-2 text-xs"
              style={{ border: '1px solid rgba(255,255,255,0.04)', background: 'rgba(11, 13, 20, 0.5)' }}
            >
              <MarkdownRenderer content={resultText} />
            </div>
          ) : null}
          {subagentToolCalls.length > 0 ? (
            <div className="mt-1">
              <ToolCallSection
                entries={subagentToolCalls}
                title={`// Tool Calls (${subagentToolCalls.length})`}
                isActive={activeToolCount > 0}
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function ChatPanel({ connected, sendMessage, sendBtwMessage, abortChat }: ChatPanelProps) {
  const activeSessionKey = useAppStore((state) => state.activeSessionKey);
  const messages = useAppStore((state) => state.messages);
  const sessions = useAppStore((state) => state.sessions);
  const streamingMessage = useAppStore((state) => state.streamingMessage);
  const thinking = useAppStore((state) => state.thinking);
  const thinkingContentMap = useAppStore((state) => state.thinkingContent);
  const toolCalls = useAppStore((state) => state.toolCalls);
  const todosBySession = useAppStore((state) => state.todosBySession);
  const pendingAttachments = useAppStore((state) => state.pendingAttachments);
  const sessionImageCountMap = useAppStore((state) => state.sessionImageCount);
  const lastReconnectAt = useAppStore((state) => state.lastReconnectAt);
  const removePendingAttachment = useAppStore((state) => state.removePendingAttachment);
  const historyLoadingMap = useAppStore((state) => state.historyLoading);
  const agents = useAppStore((state) => state.agents);
  const warningsMap = useAppStore((state) => state.warnings);
  const dismissWarning = useAppStore((state) => state.dismissWarning);

  const sessionTodos = activeSessionKey ? todosBySession[activeSessionKey] ?? EMPTY_TODOS : EMPTY_TODOS;
  const warnings = activeSessionKey ? warningsMap[activeSessionKey] ?? EMPTY_WARNINGS : EMPTY_WARNINGS;

  const agentDisplayName = useMemo(() => {
    if (!activeSessionKey) return 'Agent';
    const agentId = agentIdFromSessionKey(activeSessionKey);
    const agent = agents.find((a) => a.id === agentId);
    return agent?.identity?.name ?? agent?.name ?? agentId.charAt(0).toUpperCase() + agentId.slice(1);
  }, [activeSessionKey, agents]);

  const voiceMode = useAppStore((state) => state.voiceMode);
  const setVoiceMode = useAppStore((state) => state.setVoiceMode);

  const {
    voiceConnected,
    voiceConnecting,
    isRecording,
    voiceError,
    liveTranscript,
    activeTasks,
    connectVoice,
    disconnectVoice,
    startRecording,
    stopRecording,
  } = useVoice();

  const fontSize = useSettingsStore((state) => state.settings.editor.fontSize);
  const messageDensity = useSettingsStore((state) => state.settings.editor.messageDensity);
  const sendOnEnter = useSettingsStore((state) => state.settings.editor.sendOnEnter);

  const draftKey = activeSessionKey ? `chat:${activeSessionKey}` : '';
  const input = useAppStore((state) => draftKey ? (state.draftTexts[draftKey] ?? '') : '');
  const setDraftText = useAppStore((state) => state.setDraftText);
  const setInput = useCallback((text: string) => {
    if (draftKey) setDraftText(draftKey, text);
  }, [draftKey, setDraftText]);
  const [sending, setSending] = useState(false);
  const [nearBottom, setNearBottom] = useState(true);
  const [showNewMessagesPill, setShowNewMessagesPill] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showReconnected, setShowReconnected] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [stopError, setStopError] = useState<string | null>(null);
  const [pendingTokenWarningContent, setPendingTokenWarningContent] = useState<string | null>(null);
  const [pendingTokenWarningCount, setPendingTokenWarningCount] = useState<number | null>(null);
  // Ticker that increments every 60 s to refresh relative timestamps ("2m ago" etc.)
  const [, setTimestampTick] = useState(0);

  // Refresh relative timestamps every 60 seconds
  useEffect(() => {
    const interval = window.setInterval(() => {
      setTimestampTick((t) => t + 1);
    }, 60_000);
    return () => window.clearInterval(interval);
  }, []);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const chatLockStateRef = useRef({
    sending: false,
    hasStreaming: false,
    isThinking: false,
    hasToolCalls: false,
  });
  const stopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isMobile = useIsMobile();

  const { handleFiles, handlePaste, handleDrop, handleDragOver, canAddMore } = useFileAttachment();

  const sessionMessages = useMemo(
    () => (activeSessionKey ? messages[activeSessionKey] ?? [] : []),
    [activeSessionKey, messages],
  );

  const historyLoading = activeSessionKey ? (historyLoadingMap[activeSessionKey] ?? false) : false;

  const streamText = activeSessionKey ? streamingMessage[activeSessionKey] ?? null : null;
  const hasStreaming = Boolean(streamText && streamText.trim().length > 0);
  const isThinking = activeSessionKey ? thinking[activeSessionKey] ?? false : false;
  const thinkingText = activeSessionKey ? thinkingContentMap[activeSessionKey] ?? null : null;
  const sessionToolCalls = activeSessionKey ? toolCalls[activeSessionKey] ?? [] : [];
  const sessionImageCount = activeSessionKey ? (sessionImageCountMap[activeSessionKey] ?? 0) : 0;

  // Split tool calls: main agent vs subagent (has parentToolCallId).
  // Agent tool calls go into the Agent Output box, not the main tool call list.
  const { mainToolCalls, subagentToolCalls, agentToolEntry } = useMemo(() => {
    const main: typeof sessionToolCalls = [];
    const sub: typeof sessionToolCalls = [];
    let agentEntry: (typeof sessionToolCalls)[number] | null = null;
    for (const tc of sessionToolCalls) {
      if (tc.parentToolCallId) {
        sub.push(tc);
      } else if (isAgentToolCall(tc)) {
        agentEntry = tc;
      } else {
        main.push(tc);
      }
    }
    return { mainToolCalls: main, subagentToolCalls: sub, agentToolEntry: agentEntry };
  }, [sessionToolCalls]);

  // Show Agent Output box when there's an active/completed Agent tool call.
  const showAgentOutput = agentToolEntry != null && (agentToolEntry.status === 'running' || agentToolEntry.status === 'done');
  const agentOutputStatus = agentToolEntry?.status ?? null;

  const hasRunningToolCalls = sessionToolCalls.some((tc) => tc.status === 'running');
  const isBusy = hasStreaming || isThinking || hasRunningToolCalls;

  const showLiveAreaDecorators = isThinking || sessionToolCalls.length > 0 || hasStreaming;

  const { historicalMessages, liveFinalAssistantMessage } = useMemo(() => {
    if (!showLiveAreaDecorators || hasStreaming || sessionMessages.length === 0) {
      return {
        historicalMessages: sessionMessages,
        liveFinalAssistantMessage: null as (typeof sessionMessages)[number] | null,
      };
    }

    const last = sessionMessages[sessionMessages.length - 1];
    if (last?.role === 'assistant' && !last.isHistory) {
      return {
        historicalMessages: sessionMessages.slice(0, -1),
        liveFinalAssistantMessage: last,
      };
    }

    return {
      historicalMessages: sessionMessages,
      liveFinalAssistantMessage: null as (typeof sessionMessages)[number] | null,
    };
  }, [hasStreaming, sessionMessages, showLiveAreaDecorators]);

  const showLiveArea =
    isThinking || sessionToolCalls.length > 0 || hasStreaming || Boolean(liveFinalAssistantMessage);

  const densityClass =
    messageDensity === 'compact'
      ? 'space-y-2'
      : messageDensity === 'spacious'
        ? 'space-y-6'
        : 'space-y-4';

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    bottomRef.current?.scrollIntoView({ behavior, block: 'end' });
    setShowNewMessagesPill(false);
    setNearBottom(true);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const onScroll = () => {
      const near = isNearBottom(container);
      setNearBottom(near);
      if (near) {
        setShowNewMessagesPill(false);
      }
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      container.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    scrollToBottom('auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionKey]);

  useEffect(() => {
    if (nearBottom) {
      scrollToBottom('smooth');
      return;
    }

    setShowNewMessagesPill(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historicalMessages, streamText, isThinking, sessionToolCalls, liveFinalAssistantMessage]);

  useEffect(() => {
    if (!lastReconnectAt) {
      return;
    }

    const hadLockedChatState =
      chatLockStateRef.current.sending ||
      chatLockStateRef.current.hasStreaming ||
      chatLockStateRef.current.isThinking ||
      chatLockStateRef.current.hasToolCalls;

    setSending(false);

    if (!hadLockedChatState) {
      setShowReconnected(false);
      return;
    }

    setShowReconnected(true);

    const timer = window.setTimeout(() => {
      setShowReconnected(false);
    }, 2000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [lastReconnectAt]);

  useEffect(() => {
    chatLockStateRef.current = {
      sending,
      hasStreaming,
      isThinking,
      hasToolCalls: sessionToolCalls.length > 0,
    };
  }, [hasStreaming, isThinking, sending, sessionToolCalls]);

  // Clear stopping state when the session goes idle or changes
  useEffect(() => {
    if (!isBusy) {
      setStopping(false);
      setStopError(null);
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }
    }
  }, [isBusy]);

  useEffect(() => {
    setStopping(false);
    setStopError(null);
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
  }, [activeSessionKey]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const maxHeight = 24 * 6;
    const container = scrollContainerRef.current;
    const savedScrollTop = container?.scrollTop ?? 0;

    if (isMobile) {
      // On iOS, use rAF + clone measurement to avoid reflow that causes
      // the viewport to jump while the keyboard is open.
      requestAnimationFrame(() => {
        const clone = textarea.cloneNode(true) as HTMLTextAreaElement;
        clone.style.cssText =
          'position:absolute;visibility:hidden;height:auto;overflow:hidden;max-height:none;pointer-events:none;';
        textarea.parentNode?.appendChild(clone);
        const newHeight = Math.min(clone.scrollHeight, maxHeight);
        clone.remove();
        textarea.style.height = `${newHeight}px`;
        // Restore scroll position to prevent jump
        if (container) {
          container.scrollTop = savedScrollTop;
        }
      });
    } else {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    }
  }, [input, isMobile]);

  const handleSessionResetCommand = async (command: '/new' | '/reset' = '/new') => {
    if (!connected || !activeSessionKey || hasStreaming || sending) {
      return;
    }

    setInput('');
    setSending(true);
    try {
      await sendMessage(command);
    } finally {
      setSending(false);
    }
  };

  const clearTokenWarning = useCallback(() => {
    setPendingTokenWarningContent(null);
    setPendingTokenWarningCount(null);
  }, []);

  useEffect(() => {
    clearTokenWarning();
  }, [activeSessionKey, clearTokenWarning]);

  const maybeOpenTokenWarning = useCallback((messageText: string, hasAttachments: boolean) => {
    const tokenCount = getSessionTokenCount(sessions, activeSessionKey);
    if (!shouldShowContextTokenWarning({ messageText, hasAttachments, tokenCount })) {
      return false;
    }

    setPendingTokenWarningContent(messageText);
    setPendingTokenWarningCount(tokenCount);
    return true;
  }, [activeSessionKey, sessions]);

  const handleSend = async (overrideContent?: string, skipTokenWarning = false) => {
    const draftContent = overrideContent !== undefined ? overrideContent : input;
    const content = draftContent.trim();
    const attachments = useAppStore.getState().pendingAttachments;

    if (
      (!content && attachments.length === 0) ||
      !connected ||
      !activeSessionKey ||
      hasStreaming ||
      sending
    ) {
      return;
    }

    if (!skipTokenWarning && maybeOpenTokenWarning(draftContent, attachments.length > 0)) {
      return;
    }

    const resetMatch = content.match(/^\/(new|reset)$/i);
    if (resetMatch) {
      await handleSessionResetCommand(`/${resetMatch[1].toLowerCase()}` as '/new' | '/reset');
      return;
    }

    const btwMatch = content.match(/^\/btw(?:\s+([\s\S]+))?$/i);
    if (btwMatch) {
      const btwText = btwMatch[1]?.trim() ?? '';
      if (!btwText) {
        useAppStore.getState().addMessage(activeSessionKey, {
          role: 'system',
          content: 'Usage: /btw <message>',
          timestamp: new Date().toISOString(),
        });
        return;
      }

      if (attachments.length > 0) {
        useAppStore.getState().addMessage(activeSessionKey, {
          role: 'system',
          content: '/btw does not support attachments yet.',
          timestamp: new Date().toISOString(),
        });
        return;
      }

      const reconnectMarker = useAppStore.getState().lastReconnectAt;
      const sendingSessionKey = activeSessionKey;

      setSending(true);
      try {
        const result = await sendBtwMessage(btwText);
        useAppStore.getState().upsertSubagent(result.sessionKey, result.label, true);

        const latestState = useAppStore.getState();
        const sameConnection = latestState.lastReconnectAt === reconnectMarker;
        const sameSession = latestState.activeSessionKey === sendingSessionKey;
        if (sameConnection && sameSession) {
          setInput('');
        }
      } catch (error) {
        console.error('[ChatPanel] Failed to start /btw:', error);
        useAppStore.getState().addMessage(activeSessionKey, {
          role: 'system',
          content: toFriendlyErrorMessage(error, 'btw'),
          timestamp: new Date().toISOString(),
        });
      } finally {
        setSending(false);
      }
      return;
    }

    const reconnectMarker = useAppStore.getState().lastReconnectAt;
    const sendingSessionKey = activeSessionKey;

    setSending(true);
    let sendAttempted = false;
    try {
      const state = useAppStore.getState();
      const uploadedAttachments = attachments.length > 0
        ? await uploadPendingAttachments(attachments, state.gatewayToken)
        : [];
      const previewUrls = attachments.flatMap((attachment) => attachment.dataUrl ? [attachment.dataUrl] : []);
      const messageText = appendUploadedAttachmentsToMessage(content, uploadedAttachments);

      state.addMessage(activeSessionKey, {
        role: 'user',
        content: messageText || '(attachment)',
        timestamp: new Date().toISOString(),
        attachmentPreviews: previewUrls.length > 0 ? previewUrls : undefined,
      });

      if (attachments.length > 0) {
        state.incrementSessionImageCount(activeSessionKey, attachments.length);
      }

      sendAttempted = true;
      await sendMessage(messageText || '');

      const latestState = useAppStore.getState();
      const sameConnection = latestState.lastReconnectAt === reconnectMarker;
      const sameSession = latestState.activeSessionKey === sendingSessionKey;
      if (sameConnection && sameSession) {
        setInput('');
        latestState.clearPendingAttachments();
      }
    } catch (error) {
      console.error('[ChatPanel] Failed to send message:', error);
      if (!sendAttempted) {
        useAppStore.getState().addMessage(activeSessionKey, {
          role: 'system',
          content: toFriendlyErrorMessage(error, 'chat-send'),
          timestamp: new Date().toISOString(),
        });
      }
    } finally {
      setSending(false);
    }
  };

  const handleStop = async () => {
    if (!activeSessionKey || !isBusy || stopping) {
      return;
    }

    setStopping(true);
    setStopError(null);

    // 10s client-side timeout
    stopTimeoutRef.current = setTimeout(() => {
      setStopping(false);
      setStopError('Stop may have failed — try again');
    }, 10_000);

    try {
      const res = await abortChat(activeSessionKey);
      // Treat bare {ok:true} (no status) or "already_terminal" as success
      if (res.status === 'already_terminal' || res.status === 'not_found') {
        setStopping(false);
        if (stopTimeoutRef.current) {
          clearTimeout(stopTimeoutRef.current);
          stopTimeoutRef.current = null;
        }
      }
      // For "cancelled", wait for the aborted event to clear stopping (via isBusy effect)
    } catch {
      setStopping(false);
      setStopError('Failed to stop — try again');
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }
    }
  };

  const handleProceedTokenWarning = () => {
    const pendingContent = pendingTokenWarningContent;
    if (pendingContent === null) {
      return;
    }

    clearTokenWarning();
    void handleSend(pendingContent, true);
  };

  const handlePromptSelect = (promptText: string) => {
    // Show the text in the input field briefly before it's cleared on send
    setInput(promptText);
    void handleSend(promptText);
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (sendOnEnter) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        void handleSend();
      }
      return;
    }

    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void handleSend();
    }
  };

  return (
    <div
      className="relative flex flex-1 min-h-0 min-w-0 flex-col bg-background text-foreground"
      onDragOver={(event) => {
        event.preventDefault();
        setIsDragging(true);
        handleDragOver(event.nativeEvent as DragEvent);
      }}
      onDragEnter={(event) => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(event) => {
        if (event.currentTarget === event.target) {
          setIsDragging(false);
        }
      }}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragging(false);
        handleDrop(event.nativeEvent as DragEvent);
      }}
    >
      {isDragging ? (
        <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center rounded-xl border-2 border-dashed border-ring-light bg-primary-20">
          <span className="text-lg font-medium text-accent-foreground">Drop images here</span>
        </div>
      ) : null}

      <div
        ref={scrollContainerRef}
        className={`relative flex-1 overflow-y-auto chat-ambient ${isMobile ? 'px-3 py-4' : 'px-5 py-6'} ${densityClass}`}
        style={{ fontSize: `${fontSize}px` }}
      >
        {sessionMessages.length === 0 && !hasStreaming && !historyLoading && connected ? (
          <WelcomeScreen onPromptSelect={handlePromptSelect} />
        ) : null}

        {warnings.map((w, i) => (
          <div key={i} className="mx-4 mb-2 flex items-center justify-between rounded-lg bg-yellow-50 px-4 py-2 text-sm text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200">
            <span>{w.text}</span>
            <button
              onClick={() => activeSessionKey && dismissWarning(activeSessionKey, i)}
              className="ml-4 text-yellow-600 hover:text-yellow-800 dark:text-yellow-400"
            >
              ×
            </button>
          </div>
        ))}

        {historicalMessages.map((message, index) => {
          const isUser = message.role === 'user';
          const isSystem = message.role === 'system';

          const rowClass = isSystem ? 'justify-center' : isUser ? 'justify-end' : 'justify-start';

          const bubbleClass = isSystem
            ? 'bg-destructive border border-destructive-border text-destructive-foreground'
            : '';

          const showTs = !isSystem && shouldShowTimestamp(historicalMessages, index);
          const tsText = showTs ? formatTimestamp(message.timestamp) : '';

          return (
            <div key={`${message.timestamp ?? index}-${index}`} className={`flex ${rowClass}`}>
              <div className="max-w-full">
                {/* Sender label */}
                {!isSystem && (
                  <div className={`mb-1 ${isUser ? 'text-right' : 'text-left'}`} style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: isUser ? 'var(--theme-muted-foreground)' : 'var(--theme-primary)' }}>
                    {isUser ? 'You' : agentDisplayName.toUpperCase()}
                  </div>
                )}
                <div
                  className={`px-4 py-2 break-words ${bubbleClass}`}
                  style={
                    isSystem
                      ? undefined
                      : isUser
                        ? {
                            backgroundColor: 'var(--theme-user-bubble-background)',
                            color: 'var(--theme-user-bubble-text)',
                            borderRadius: '6px',
                            borderLeft: '2px solid rgba(255, 123, 53, 0.3)',
                          }
                        : {
                            backgroundColor: 'var(--theme-assistant-bubble-background)',
                            color: 'var(--theme-assistant-bubble-text)',
                            borderRadius: '6px',
                          }
                  }
                >
                  {isUser || isSystem ? (
                    <span className="whitespace-pre-wrap">{message.content}</span>
                  ) : (
                    <MarkdownRenderer content={message.content} />
                  )}

                  {isUser
                    ? message.attachmentPreviews?.map((url, imageIndex) => (
                        <img
                          key={imageIndex}
                          src={url}
                          alt="Attached image"
                          className="mt-2 max-h-60 max-w-full rounded-lg"
                        />
                      ))
                    : null}

                  {!isUser && !isSystem
                    ? message.images?.map((image, imageIndex) => {
                        const src = `data:${image.source.media_type};base64,${image.source.data}`;
                        return (
                          <img
                            key={imageIndex}
                            src={src}
                            alt="Assistant image"
                            className="mt-2 max-h-80 max-w-full cursor-pointer rounded-lg"
                            onClick={() => window.open(src, '_blank')}
                          />
                        );
                      })
                    : null}
                </div>

                {isUser && message.injectedContext ? (
                  <InjectedContextToggle content={message.injectedContext} />
                ) : null}

                {!isUser && !isSystem && message.toolCalls && message.toolCalls.length > 0 ? (() => {
                  const subTc = message.toolCalls!.filter((tc) => tc.parentToolCallId);
                  const agentTc = message.toolCalls!.filter((tc) => isAgentToolCall(tc) && !tc.parentToolCallId);
                  const otherMainTc = message.toolCalls!.filter((tc) => !isAgentToolCall(tc) && !tc.parentToolCallId);
                  const hasAgentOutput = agentTc.length > 0 || subTc.length > 0;
                  return (
                    <>
                      {hasAgentOutput ? (
                        <CommittedAgentOutput agentToolCalls={agentTc} subagentToolCalls={subTc} />
                      ) : null}
                      {otherMainTc.length > 0 ? (
                        <ToolCallSection entries={otherMainTc} isCommitted />
                      ) : null}
                    </>
                  );
                })() : null}

                {!isUser &&
                !isSystem &&
                message.thinkingBlocks &&
                message.thinkingBlocks.length > 0 ? (
                  <ThinkingIndicator
                    isThinking={false}
                    blocks={message.thinkingBlocks}
                    isCommitted
                  />
                ) : null}

                {showTs && tsText ? (
                  <div
                    className={`mt-1 px-1 ${isUser ? 'text-right' : 'text-left'}`}
                    style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.58rem', color: 'var(--theme-muted-foreground)' }}
                  >
                    {tsText}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}

        {showLiveArea ? (
          <div className="space-y-2">
            {showAgentOutput ? (
              <div className="flex justify-start">
                <AgentOutputBox
                  isRunning={agentOutputStatus === 'running'}
                  resultText={agentToolEntry?.partialResult ?? (typeof agentToolEntry?.result === 'string' ? agentToolEntry.result : null)}
                  subagentToolCalls={subagentToolCalls}
                />
              </div>
            ) : null}

            {mainToolCalls.length > 0 ? (
              <div className="flex justify-start">
                <ToolCallSection entries={mainToolCalls} defaultExpanded />
              </div>
            ) : null}

            <div className="flex justify-start">
              <ThinkingIndicator isThinking={isThinking} content={thinkingText} />
            </div>

            {hasStreaming ? (
              <div className="flex justify-start">
                <div className="max-w-full">
                  <div className="mb-1 text-left" style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--theme-primary)' }}>
                    Agent
                  </div>
                  <div
                    className="max-w-full px-4 py-2"
                    style={{
                      backgroundColor: 'var(--theme-assistant-bubble-background)',
                      color: 'var(--theme-assistant-bubble-text)',
                      borderRadius: '6px',
                    }}
                  >
                    <MarkdownRenderer content={streamText ?? ''} isStreaming />
                    <span className="streaming-dot" />
                  </div>
                  <div className="mt-1 px-1 text-left" style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.58rem', color: 'var(--theme-muted-foreground)' }}>just now</div>
                </div>
              </div>
            ) : liveFinalAssistantMessage ? (
              <div className="flex justify-start">
                <div className="max-w-full">
                  <div className="mb-1 text-left" style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--theme-primary)' }}>
                    Agent
                  </div>
                  <div
                    className="max-w-full px-4 py-2"
                    style={{
                      backgroundColor: 'var(--theme-assistant-bubble-background)',
                      color: 'var(--theme-assistant-bubble-text)',
                      borderRadius: '6px',
                    }}
                  >
                    <MarkdownRenderer content={liveFinalAssistantMessage.content} />
                    {liveFinalAssistantMessage.images?.map((image, imageIndex) => {
                      const src = `data:${image.source.media_type};base64,${image.source.data}`;
                      return (
                        <img
                          key={imageIndex}
                          src={src}
                          alt="Assistant image"
                          className="mt-2 max-h-80 max-w-full cursor-pointer rounded-lg"
                          onClick={() => window.open(src, '_blank')}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <div ref={bottomRef} />

        {showNewMessagesPill && !nearBottom ? (
          <button
            type="button"
            onClick={() => scrollToBottom('smooth')}
            className="sticky bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-ring-70 bg-primary-90 px-3 py-1 text-xs font-medium text-primary-foreground shadow-lg hover:bg-primary-hover"
          >
            ↓ New messages
          </button>
        ) : null}
      </div>

      {sessionTodos.length > 0 ? (
        <div className={`${isMobile ? 'px-2 pt-2' : 'px-4 pt-3'}`}>
          <TodoTaskList todos={sessionTodos} />
        </div>
      ) : null}

      {activeTasks.length > 0 ? (
        <div className={`flex flex-col ${isMobile ? 'px-2 pt-2 -mb-2' : 'px-4 pt-3 -mb-3'}`}>
          {activeTasks.map((task) => (
            <div
              key={task.taskId}
              className={`transition-all duration-500 ease-in-out ${
                task.dismissing ? 'opacity-0 scale-95 translate-y-2' : 'opacity-100 scale-100 translate-y-0'
              } animate-in fade-in slide-in-from-bottom-2`}
            >
              <TaskStatusPanel
                taskId={task.taskId}
                label={task.label}
                phase={task.phase}
                text={task.text}
              />
            </div>
          ))}
        </div>
      ) : null}

      {/* Subagent status pills */}
      <SubagentBar />

      {/* Builder-only status bar */}
      <BuilderStatusBar />

      {pendingAttachments.length > 0 ? (
        <div className={`border-t border-border ${isMobile ? 'px-2 pt-2' : 'px-4 pt-3'}`}>
          <AttachmentPreviewBar
            attachments={pendingAttachments}
            onRemove={removePendingAttachment}
            sentCount={sessionImageCount}
            maxCount={MAX_CHAT_ATTACHMENTS}
          />
        </div>
      ) : null}

      <div className={`chat-composer ${isMobile ? 'px-2 pt-1' : 'px-4'}`}>
        {showReconnected ? (
          <div className="mb-2 text-xs font-medium text-emerald-400">Reconnected. You can keep chatting.</div>
        ) : null}

        {voiceMode && (voiceConnecting || voiceError || liveTranscript) ? (
          <div className={`mb-2 text-xs ${voiceError ? 'text-destructive-foreground' : 'text-muted-foreground'}`}>
            {voiceError ? voiceError : voiceConnecting ? 'Connecting voice...' : null}
            {liveTranscript ? (
              <div className="mt-1 rounded-lg bg-card px-3 py-1.5 text-sm text-foreground">{liveTranscript}</div>
            ) : null}
          </div>
        ) : null}

        <div className={`chat-composer-row flex ${isMobile ? 'gap-2' : 'gap-3'} items-center`}>
          <input
            ref={fileInputRef}
            type="file"
            accept={CHAT_UPLOAD_ACCEPT}
            multiple
            className="hidden"
            onChange={(event) => {
              if (event.target.files) {
                void handleFiles(event.target.files);
              }
              event.target.value = '';
            }}
          />

          {!voiceMode ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={!connected || !activeSessionKey || !canAddMore}
              className="self-center flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border text-muted-foreground transition disabled:cursor-not-allowed disabled:opacity-40"
              style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'var(--theme-card)', transition: 'all 0.2s ease' }}
              title="Attach file"
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.14)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)')}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="3" y="5" width="18" height="14" rx="2.5" />
                <circle cx="9" cy="10" r="1.8" />
                <path d="M6 16l4.2-4.2a1.5 1.5 0 0 1 2.1 0L18 17" />
              </svg>
            </button>
          ) : null}

          <button
            type="button"
            onClick={() => {
              const next = !voiceMode;
              setVoiceMode(next);
              if (next) {
                connectVoice();
              } else {
                disconnectVoice();
              }
            }}
            disabled={!connected || !activeSessionKey}
            className="self-center flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border text-muted-foreground transition disabled:cursor-not-allowed disabled:opacity-40"
            style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'var(--theme-card)', transition: 'all 0.2s ease' }}
            title={voiceMode ? 'Switch to text input' : 'Switch to voice input'}
            onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.14)')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)')}
          >
            {voiceMode ? (
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M7 9h.01M10 9h.01M13 9h.01M16 9h.01M7 13h10" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 1 0 6 0V5a3 3 0 0 0-3-3z" />
                <path d="M19 11a7 7 0 0 1-14 0" />
                <path d="M12 18v4" />
              </svg>
            )}
          </button>

          {voiceMode ? (
            <button
              type="button"
              disabled={!voiceConnected}
              className={`h-12 flex-1 rounded-xl border text-sm font-medium select-none ${
                isRecording
                  ? 'voice-recording-pulse border-ring bg-primary text-primary-foreground'
                  : 'border-input bg-card text-secondary-foreground hover:border-ring hover:text-foreground'
              } disabled:cursor-not-allowed disabled:opacity-40`}
              onPointerDown={(e) => {
                e.preventDefault();
                void startRecording();
              }}
              onPointerUp={(e) => {
                e.preventDefault();
                stopRecording();
              }}
              onPointerCancel={(e) => {
                e.preventDefault();
                stopRecording();
              }}
              onPointerLeave={(e) => {
                e.preventDefault();
                stopRecording();
              }}
            >
              {isRecording ? 'RECORDING...' : 'HOLD TO TALK'}
            </button>
          ) : (
            <>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={(event) => {
                  handlePaste(event.nativeEvent as ClipboardEvent);
                }}
                rows={1}
                disabled={!connected || !activeSessionKey}
                placeholder={
                  !connected
                    ? 'Connect to gateway first'
                    : !activeSessionKey
                      ? 'Select a session'
                      : 'Message your agent...'
                }
                className="self-center min-h-10 max-h-40 flex-1 resize-none rounded-lg px-3 py-2 text-foreground outline-none disabled:opacity-50"
                style={{
                  fontSize: `${isMobile ? Math.max(fontSize, 16) : fontSize}px`,
                  fontFamily: input ? 'Inter, sans-serif' : 'var(--app-ui-font)',
                  background: 'var(--theme-card)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  color: 'var(--theme-foreground)',
                }}
                onFocus={e => {
                  e.target.style.borderColor = 'rgba(255, 123, 53, 0.4)';
                  e.target.style.boxShadow = '0 0 0 2px rgba(255, 123, 53, 0.12)';
                }}
                onBlur={e => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.08)';
                  e.target.style.boxShadow = 'none';
                }}
              />

              <div className="relative self-center flex-shrink-0">
                {isBusy ? (
                  <button
                    type="button"
                    aria-label={stopError ?? 'Stop current response'}
                    title={stopError ?? 'Stop current response'}
                    onClick={() => void handleStop()}
                    disabled={stopping}
                    className="h-10 rounded-lg px-4 font-medium transition disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em', background: 'var(--destructive)', color: 'var(--destructive-foreground)', transition: 'all 0.2s ease' }}
                  >
                    <span className="flex items-center gap-1.5">
                      <svg viewBox="0 0 24 24" className={`h-3.5 w-3.5 ${stopping ? 'animate-pulse' : ''}`} fill="currentColor" aria-hidden="true">
                        <rect x="6.5" y="6.5" width="11" height="11" rx="1.5" />
                      </svg>
                      {stopping ? 'Stopping…' : stopError ? 'Retry Stop' : 'Stop'}
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => void handleSend()}
                    disabled={
                      !connected ||
                      !activeSessionKey ||
                      (input.trim().length === 0 && pendingAttachments.length === 0) ||
                      sending
                    }
                    className="h-10 rounded-lg px-4 font-medium disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em', background: 'var(--theme-primary)', color: 'var(--theme-primary-foreground)', transition: 'all 0.2s ease' }}
                    onMouseEnter={e => !e.currentTarget.disabled && (e.currentTarget.style.background = 'var(--theme-primary-hover)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'var(--theme-primary)')}
                  >
                    {sending ? 'Sending...' : 'Send'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <ContextTokenWarningDialog
        tokenCount={pendingTokenWarningCount}
        onCancel={clearTokenWarning}
        onProceed={handleProceedTokenWarning}
      />
    </div>
  );
}
